import React from 'react';
import { DragSource } from 'react-dnd';
import { DropdownButton, MenuItem, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router';
import { defineMessages, injectIntl } from 'react-intl';
import moment from 'moment';
import _ from 'lodash';
import classnames from 'classnames';

import { DATE_FORMAT } from '../../../../config';
import { CALL } from '../../../../helpers/dragTypes';
import EditableEventText from '../EditableEventText';
import AddCallNoteInline from '../AddCallNoteInline';

const messages = defineMessages({
  CALL_DURATION: { id: 'CustomerSinglePage.CALL_DURATION', defaultMessage: 'Anruf ({duration}s)' },
  PART_OF_PROJECT: { id: 'CustomerSinglePage.PART_OF_PROJECT', defaultMessage: 'ist Teil von' },
  ASSIGN_TO_PROJECT: { id: 'Call.ASSIGN_TO_PROJECT', defaultMessage: 'Zu Projekt zuordnen' },
  TITLE_CLICK_AND_DRAG: { id: 'Call.TITLE_CLICK_AND_DRAG', defaultMessage: 'Klicken und ziehen Sie den Anruf auf ein Projekt, um ihn dem Projekt zuzuordnen.' },
  SERVICE_PROTOCOL_PDF: { id: 'CustomerSinglePage.SERVICE_PROTOCOL_PDF', defaultMessage: 'Service-Protokoll (PDF)' },
  TOOLTIP_PDF_EXPORT: { id: 'CustomerSinglePage.TOOLTIP_PDF_EXPORT', defaultMessage: 'Diese PDF-Funktion erstellt ein chronologisches Protokoll über ein oder mehrere geführte Gespräche. Im Gegensatz zum Service-Protokoll ist hierbei keine Unterschrift möglich. Es dokumentiert zudem sämtliche Aktivitäten und Gespräche, die innerhalb des ausgewählten Projektordners (siehe linke Leiste) gespeichert wurden.' },
  CALL_BEGIN: { id: 'CustomerSinglePage.CALL_BEGIN', defaultMessage: '📞 Gesprächsbeginn' },
  CALL_END: { id: 'CustomerSinglePage.CALL_END', defaultMessage: '🔚 Gesprächsende' },
  PICTURE_UPLOADED: { id: 'CustomerSinglePage.PICTURE_UPLOADED', defaultMessage: '📸 Foto hochgeladen' },
  NOTE: { id: 'CustomerSinglePage.NOTE', defaultMessage: '📝 Notiz' },
});

const callSource = {
  beginDrag(props) {
    return props.call;
  },
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const item = monitor.getItem();
    const dropResult = monitor.getDropResult();
    if (props.addCallToOrder) {
      props.addCallToOrder(item, dropResult);
    }
  },
  canDrag(props) {
    return props.activeOrders && props.activeOrders.length > 0;
  },
};

@DragSource(CALL, callSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging(),
}))
@injectIntl
class AlternativeCallCard extends React.Component {
  render() {
    const {
      call,
      customer,
      activeOrders,
      isDragging,
      connectDragSource,
      connectDragPreview,
      intl: { formatMessage },
    } = this.props;

    const events = call.events || [];
    const eventsToRender = events.filter(e => e.call_event_type !== 'end');
    
    // Setup drag preview
    connectDragPreview(<h1>{formatMessage(messages.CALL_DURATION, { duration: call.duration || 0 })}</h1>);

    const callLabel = formatMessage(messages.CALL_DURATION, { duration: call.duration || 0 });
    const formattedDate = new Date(call.created || Date.now()).toLocaleString('de-DE');

    return (
      <div 
        className={classnames('call', { dragging: isDragging })} 
        style={{ 
          padding: '12px', 
          background: 'var(--panel-bg)', 
          border: '1px solid var(--panel-border)', 
          borderRadius: '12px', 
          color: 'var(--text-color)',
          opacity: isDragging ? 0.5 : 1,
          position: 'relative'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {formattedDate}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {activeOrders && activeOrders.length ? (
              <DropdownButton
                pullRight
                title=""
                id={`call${call.id}`}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  padding: '0 4px', 
                  color: 'var(--text-muted)',
                  boxShadow: 'none',
                  outline: 'none'
                }}
              >
                <MenuItem header id="Zu Projekt zuordnen">{formatMessage(messages.ASSIGN_TO_PROJECT)}</MenuItem>
                {activeOrders.map(order => (
                  <MenuItem
                    key={order.id}
                    eventKey={order.id}
                    onClick={() => this.props.addCallToOrder(call, order)}
                  >
                    {order.name}
                  </MenuItem>
                ))}
              </DropdownButton>
            ) : null}
            {activeOrders && activeOrders.length ? (
              connectDragSource(
                <i
                  className="drag-handle fa fa-th"
                  style={{ cursor: 'move', color: 'var(--text-muted)', fontSize: '14px' }}
                  title={formatMessage(messages.TITLE_CLICK_AND_DRAG)}
                />
              )
            ) : null}
          </div>
        </div>

        <div style={{ fontSize: '13px' }}>
          {callLabel}
          {call.order_name && (
            <span>
              {' '}
              {formatMessage(messages.PART_OF_PROJECT)}{' '}
              <Link
                title={call.order_name}
                style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 'inherit' }}
                to={`/company/${customer.company_id}/customer/${customer.id}/order/${call.order_id}`}
              >
                {call.order_name}
              </Link>
            </span>
          )}
        </div>

        {/* Mapped events inside Call */}
        {events && events.length > 0 && (
          <div style={{ marginTop: '10px', paddingLeft: '10px', borderLeft: '2px solid var(--panel-border)' }}>
            {events.sort((a, b) => new Date(a.created || 0) - new Date(b.created || 0)).map((subEvt, k) => {
              let label = subEvt.call_event_type;
              if (label === 'call_begin' || label === 'begin') label = formatMessage(messages.CALL_BEGIN);
              else if (label === 'end') label = formatMessage(messages.CALL_END);
              else if (label === 'picture') label = formatMessage(messages.PICTURE_UPLOADED);
              else if (label === 'note') label = formatMessage(messages.NOTE);
              
              const timeStr = new Date(subEvt.created || Date.now()).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
              
              return (
                <div key={k} style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px' }}>{timeStr}</div>
                  <div style={{ fontSize: '13px' }}>
                    {subEvt.text && subEvt.id !== Infinity && (subEvt.call_event_type === 'note' || subEvt.call_event_type === 'end_note') ? (
                      <EditableEventText 
                        text={subEvt.text} 
                        label={label}
                        eventId={subEvt.id} 
                        onSave={(id, value) => this.props.updateEvent(id, { text: value }).then(() => this.props.load())} 
                      />
                    ) : (
                      subEvt.text ? (
                        <span><strong>{label}:</strong> {subEvt.text}</span>
                      ) : (
                        <strong>{label}</strong>
                      )
                    )}
                  </div>
                  {subEvt.pictures && subEvt.pictures.map((pic, l) => (
                    <img key={`subpic-${l}`} src={pic.path} alt="Upload" style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '6px' }} />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Call notes action and Service-Protokoll PDF */}
        <div style={{ marginTop: '12px' }}>
          <AddCallNoteInline 
            callId={call.id} 
            onAddNote={(callId, value) => {
              return this.props.createNoteWithCall({ text: value }, { id: callId }).then(() => this.props.load());
            }} 
          />
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '8px', width: '100%' }}>
            <button 
              onClick={() => this.props.handleGeneratePdf(call.id)}
              className="lh-btn lh-btn-small lh-btn-default"
              style={{ height: '34px', boxSizing: 'border-box' }}
            >
              <i className="fa fa-file-pdf-o" style={{ marginRight: '6px' }}></i>
              {formatMessage(messages.SERVICE_PROTOCOL_PDF)}
            </button>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="pdf-export-tooltip" style={{ maxWidth: 320, textAlign: 'left', fontSize: 12 }}>
                  {formatMessage(messages.TOOLTIP_PDF_EXPORT)}
                </Tooltip>
              }
            >
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                background: 'var(--primary-color)', 
                color: 'white', 
                fontSize: 11, 
                fontWeight: 'bold', 
                cursor: 'help',
                marginLeft: '8px'
              }}>?</span>
            </OverlayTrigger>
          </div>
        </div>
      </div>
    );
  }
}

export default AlternativeCallCard;
